import SubScriptionManagerBase from "./subscriptionmanagerbase";
import SubScriptionManagerSSO from "./subscriptionmanagersso";
import SubscriptionManagerState from "./subscriptionManagerState";
import { AUTHENTICATED } from "./../../common/analytics/analyticsevents";
import AuthenticationAuthorizationError from "../utilities/errors/authenticationAuthorizationError";
import {
  EErrorOrigin,
  EErrorType,
  EValidateSubscriptionErrorCode,
  EAuthenticationType,
} from "./../../common/utilities/enums";
import CookieManager from "../utilities/cookiemanager";

// this class will implement al the necessary API communication
// and cookie management to authenticate users and authorize
// downloads. It does not implement any UI elements.
export default class SubScriptionManagerSubscription extends SubScriptionManagerBase {
  constructor(
    appState,
    sessionManager,
    apiManager,
    cookieManager,
    options = {}
  ) {
    super(appState, sessionManager, apiManager, cookieManager, options);
  }

  get authenticationFlowType() {
    if (this._options.SSOEnabled) {
      if (this._options.generateSSOState) {
        return EAuthenticationType.STATE;
      }
      return EAuthenticationType.SSO;
    } else {
      return EAuthenticationType.DEFAULT;
    }
  }

  // returns a promise that will either reject with an error code when authentication
  // fails, either resolves to success
  // in any case it will also trigger the authenticationstatuschanged event.
  authenticateByUserNamePassword(username, password, downloadToken = "") {
    const authenticationPromise = new Promise((resolve, reject) => {
      // check if there is an SSO cookie if the settings are SSO enabled,
      // else check for the subscription cookie.
      this._sessionManager
        .getSessionInfo()
        .then((sessionId) => {
          this._apiManager
            .validateSubscription(
              sessionId,
              downloadToken,
              username,
              password,
              this._options.subscriptionOptions.subscriptionProvider,
              "1.0.0.0",
              this._sessionManager.userId,
              this._options.subscriptionOptions.authenticationProvider
            )
            .then((data) => {
              if (data.Status !== "Succes") {
                this.removeAuthenticationCookies();
                reject(
                  new AuthenticationAuthorizationError(
                    data,
                    EErrorOrigin.VALIDATESUBSCRIPTION,
                    null,
                    EErrorType.AUTHENTICATION
                  )
                );
              } else {
                data.username = username;
                this._authenticatedSucces(data);
                this._appState.triggerEvent(AUTHENTICATED);
                resolve();
              }
            })
            .catch((data) => {
              reject(
                new AuthenticationAuthorizationError(
                  data,
                  EErrorOrigin.VALIDATESUBSCRIPTION,
                  EValidateSubscriptionErrorCode.CALL_FAILED
                )
              );
            });
        })
        .catch((error) => {
          this.removeAuthenticationCookies();
          reject(error);
        });
    });
    return authenticationPromise;
  }

  authenticateViaSSO = (downloadToken) => {
    const authenticationPromise = new Promise((resolve, reject) => {
      this.sso = new SubScriptionManagerSSO(
        this._appState,
        this._sessionManager,
        this._apiManager,
        this._cookieManager,
        this._options
      );
      this.sso
        .authenticate(downloadToken)
        .then((data) => {
          if (data.Status.toLowerCase() === "success") {
            this._authenticatedSucces(data);
            resolve(data);
          } else {
            this._authenticateUsingCookie(resolve, reject);
          }
        })
        .catch(() => {
          this._authenticateUsingCookie(resolve, reject);
        });
    });

    return authenticationPromise;
  };

  authenticateViaState = (downloadToken) => {
    const authenticationPromise = new Promise((resolve, reject) => {
      this.stateAuthentication = new SubscriptionManagerState(
        this._appState,
        this._sessionManager,
        this._apiManager,
        this._cookieManager,
        this._options
      );

      this.stateAuthentication
        .authenticate(downloadToken)
        .then((data) => {
          if (data.Status.toLowerCase() === "success") {
            this._authenticatedSucces(data);
            resolve(data);
          } else {
            this._authenticateUsingCookie(resolve, reject);
          }
        })
        .catch(() => {
          this._authenticateUsingCookie(resolve, reject);
        });
    });

    return authenticationPromise;
  };

  authenticateViaTwipe = () => {
    const authenticationPromise = new Promise((resolve, reject) => {
      this._authenticateUsingCookie(resolve, reject);
    });

    return authenticationPromise;
  };

  _authenticatedSucces(data) {
    // store subscriptionCookie
    this._createSubscriptionCookie(
      data.SubscriptionID,
      data.SubscriptionReference,
      data.SubscriptionUserReference,
      data.SubscriptionType,
      data.username
    );
    this.trigger("subscriptionmanager:authenticationstatuschanged");
    if (this.stateAuthentication) {
      this.stateAuthentication.redirect();
    }
  }

  // returns a promise that will either reject with an error code when authentication
  // fails, either resolves to success. This is to be used in case of SSO, or
  // to check if you are autenticated
  // in any case it will also trigger the authenticationstatuschanged event if
  // the current status changed.
  // returns subsID
  authenticate(downloadToken) {
    switch (this.authenticationFlowType) {
      case EAuthenticationType.SSO:
        return this.authenticateViaSSO(downloadToken);
      case EAuthenticationType.STATE:
        return this.authenticateViaState(downloadToken);
      default:
        return this.authenticateViaTwipe();
    }
  }

  _authenticateUsingCookie(resolve, reject) {
    const loginCookie = CookieManager._getLoginCookie();
    if (loginCookie) {
      this._updateAppStateUserInfo(loginCookie);
      //TODO: check if still good subscription? using hash
      this.trigger("subscriptionmanager:authenticationstatuschanged");
      resolve(loginCookie.subsId);
    } else {
      this.removeAuthenticationCookies();
      reject("no authentication cookie");
    }
  }

  getSubscriptionIdFromAuthenticationResponse = (response) => {
    if (
      typeof response === "object" &&
      response.hasOwnProperty("SubscriptionID")
    ) {
      return response.SubscriptionID;
    }
    return response;
  };

  getSSOState() {
    if (this.stateAuthentication) {
      return this.stateAuthentication.getSSOState();
    }
    return;
  }

  createSSOStateCookie(redirectUrl) {
    if (this.stateAuthentication) {
      this.stateAuthentication.setSSOState(redirectUrl);
    }
  }

  removeSSOStateCookie() {
    if (this.stateAuthentication) {
      this.stateAuthentication.removeSSOStateCookie();
    }
  }
}



// WEBPACK FOOTER //
// ./src/common/subscriptionmanagement/subscriptionmanagersubscription.js